import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Check, ChevronsUpDown, Database, KeyRound, Pencil, Search, ShieldCheck, SlidersHorizontal, Users, X } from "lucide-react";
import { Button } from "@web/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@web/components/ui/card";
import { Input } from "@web/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { cn } from "@web/lib/utils";
import {
  changeOwnPassword,
  createAdminUser,
  getAdminUsers,
  resetAdminUserPassword,
  saveAdminUserGroups,
  updateAdminUser,
  type SourceManifest,
  type UserGroup,
  type UserSpace,
} from "../api";
import type { ReaderWorkspace } from "../reader/reader-workspace";
import { SourceManagementPanel } from "../sources/source-management-dialog";
import { createDefaultSourceGroups, createSourceGroupTabs, getMaxSourceGroupCount, toPersistedSourceGroups } from "../sources/source-groups";

interface UserManagementPageProps {
  workspace: ReaderWorkspace;
}

type UserFilter = "all" | "active" | "disabled" | "admin" | "user";
type UserSort = "created-desc" | "created-asc" | "username-asc" | "username-desc";
type AdminSection = "profile" | "accounts" | "groups";

const userFilterOptions: Array<{ value: UserFilter; label: string }> = [
  { value: "all", label: "全部" },
  { value: "active", label: "已启用" },
  { value: "disabled", label: "已停用" },
  { value: "admin", label: "管理员" },
  { value: "user", label: "普通用户" },
];

const userSortOptions: Array<{ value: UserSort; label: string }> = [
  { value: "created-desc", label: "最新创建" },
  { value: "created-asc", label: "最早创建" },
  { value: "username-asc", label: "用户名升序" },
  { value: "username-desc", label: "用户名降序" },
];

const userRoleOptions: Array<{ value: UserSpace["role"]; label: string }> = [
  { value: "admin", label: "管理员" },
  { value: "user", label: "用户" },
];

const userStatusOptions: Array<{ value: UserSpace["status"]; label: string }> = [
  { value: "active", label: "启用" },
  { value: "disabled", label: "停用" },
];

export function UserManagementPage({ workspace }: UserManagementPageProps) {
  const [users, setUsers] = useState<UserSpace[]>([]);
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [displayNameDraft, setDisplayNameDraft] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [nextPassword, setNextPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<UserFilter>("all");
  const [sort, setSort] = useState<UserSort>("created-desc");
  const [activeSection, setActiveSection] = useState<AdminSection>(() => readAdminSectionFromLocation());
  const isAdmin = workspace.user?.role === "admin";
  const selectedUser = users.find(user => user.id === selectedUserId);
  const sourceById = useMemo(() => new Map(workspace.sources.map(source => [source.id, source])), [workspace.sources]);
  const sourceTabs = useMemo(() => createSourceGroupTabs(workspace.sources, workspace.sourceGroups), [workspace.sourceGroups, workspace.sources]);
  const visibleUsers = useMemo(() => filterAndSortUsers(users, query, filter, sort), [filter, query, sort, users]);

  useEffect(() => {
    if (!isAdmin) return;
    void refresh();
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin || activeSection === "profile") return;
    setActiveSection("profile");
  }, [activeSection, isAdmin]);

  useEffect(() => {
    const onlyEmptyAllGroup = workspace.sourceGroups.length === 1 && workspace.sourceGroups[0]?.id === "all" && workspace.sourceGroups[0].sourceIds.length === 0;
    if (workspace.sources.length === 0 || workspace.sourceGroups.length > 0 && !onlyEmptyAllGroup) return;
    void workspace.handleSaveSourceGroups(createDefaultSourceGroups(workspace.sources));
  }, [workspace.handleSaveSourceGroups, workspace.sourceGroups, workspace.sources]);

  useEffect(() => {
    const handleRouteChange = () => setActiveSection(readAdminSectionFromLocation());
    window.addEventListener("popstate", handleRouteChange);
    return () => window.removeEventListener("popstate", handleRouteChange);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (activeSection === "profile") url.searchParams.delete("section");
    else url.searchParams.set("section", activeSection);
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [activeSection]);

  useEffect(() => {
    setDisplayNameDraft(selectedUser?.displayName ?? "");
  }, [selectedUser?.displayName, selectedUser?.id]);

  async function refresh() {
    const dashboard = await getAdminUsers();
    setUsers(dashboard.users);
    setGroups(dashboard.groups);
  }

  async function handleChangeOwnPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!nextPassword || nextPassword !== confirmPassword) {
      const confirmInput = event.currentTarget.elements.namedItem("confirmPassword");
      if (confirmInput instanceof HTMLInputElement) {
        confirmInput.setCustomValidity("两次输入的新密码不一致");
        confirmInput.reportValidity();
        confirmInput.setCustomValidity("");
      }
      return;
    }

    await changeOwnPassword(currentPassword, nextPassword);
    setCurrentPassword("");
    setNextPassword("");
    setConfirmPassword("");
    await workspace.handleLogout();
  }

  async function handleCreateUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const user = await createAdminUser({
      username: newUsername,
      password: newPassword,
      displayName: newDisplayName,
    });

    setNewUsername("");
    setNewDisplayName("");
    setNewPassword("");
    setCreateOpen(false);
    setSelectedUserId(user.id);
    await refresh();
  }

  async function handleAddGroup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = newGroupName.trim();
    if (!name) return;
    const nextGroups = await saveAdminUserGroups([
      ...groups.map(group => ({ id: group.id, name: group.name, sourceIds: group.sourceIds })),
      { id: `group-${Date.now().toString(36)}`, name, sourceIds: [] },
    ]);

    setNewGroupName("");
    setGroups(nextGroups);
  }

  async function handleDeleteGroup(groupId: string) {
    const nextGroups = await saveAdminUserGroups(groups.filter(group => group.id !== groupId).map(group => ({
      id: group.id,
      name: group.name,
      sourceIds: group.sourceIds,
    })));

    setGroups(nextGroups);
    setUsers(current => current.map(user => ({ ...user, groupIds: user.groupIds.filter(id => id !== groupId) })));
    for (const user of users.filter(user => user.groupIds.includes(groupId))) {
      await updateAdminUser(user.id, { groupIds: user.groupIds.filter(id => id !== groupId) });
    }
  }

  async function handleToggleGroupSource(group: UserGroup, sourceId: string) {
    await handleSetGroupSources(group, toggleId(group.sourceIds, sourceId));
  }

  async function handleSetGroupSources(group: UserGroup, sourceIds: string[]) {
    const nextGroups = await saveAdminUserGroups(groups.map(item => ({
      id: item.id,
      name: item.name,
      sourceIds: item.id === group.id ? sourceIds : item.sourceIds,
    })));

    setGroups(nextGroups);
  }

  async function handleUpdateUser(user: UserSpace, patch: Partial<Pick<UserSpace, "displayName" | "role" | "status" | "groupIds" | "sourceIds">>) {
    const updated = await updateAdminUser(user.id, patch);
    setUsers(current => current.map(item => (item.id === updated.id ? updated : item)));
  }

  async function handleResetPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedUser || selectedUser.username === "root" || !resetPassword.trim()) return;
    await resetAdminUserPassword(selectedUser.id, resetPassword);
    setResetPassword("");
  }

  async function handleCheckSources(sources: SourceManifest[]) {
    for (const source of sources) {
      await workspace.handleCheckSource(source.id);
    }
  }

  async function handleSetSourcesEnabled(sources: SourceManifest[], enabled: boolean) {
    const enabledSourceIds = new Set(workspace.enabledSourceIds);

    for (const source of sources) {
      if (enabledSourceIds.has(source.id) === enabled) continue;
      await workspace.handleToggleSource(source.id);
    }
  }

  async function handleCreateSourceGroup(label: string, sources: SourceManifest[]) {
    const trimmedLabel = label.trim();
    if (!trimmedLabel || sourceTabs.length >= getMaxSourceGroupCount()) return;

    await workspace.handleSaveSourceGroups(toPersistedSourceGroups(workspace.sources, [
      ...sourceTabs,
      {
        id: `custom-${Date.now().toString(36)}`,
        label: trimmedLabel,
        builtIn: false,
        sourceIds: sources.map(source => source.id),
        sources,
      },
    ]));
  }

  async function handleDeleteSourceGroup(groupId: string) {
    await workspace.handleSaveSourceGroups(toPersistedSourceGroups(workspace.sources, sourceTabs.filter(group => group.id !== groupId)));
  }

  async function handleUpdateSourceGroupSourceIds(groupId: string, sourceIds: string[]) {
    await workspace.handleSaveSourceGroups(toPersistedSourceGroups(workspace.sources, sourceTabs.map(group => (group.id === groupId ? { ...group, sourceIds } : group))));
  }

  async function handleSaveSourceGroups(groups: typeof sourceTabs) {
    await workspace.handleSaveSourceGroups(toPersistedSourceGroups(workspace.sources, groups));
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
      <AdminSidebar activeSection={activeSection} isAdmin={isAdmin} onSectionChange={setActiveSection} />

      <div className="min-w-0 space-y-4">
        {activeSection === "profile" && (
          <>
            <PersonalManagementPanel
              currentUser={workspace.user}
              prefetchChapterCount={workspace.preference.prefetchChapterCount}
              currentPassword={currentPassword}
              nextPassword={nextPassword}
              confirmPassword={confirmPassword}
              onPrefetchChapterCountChange={value => void workspace.handlePreferenceChange({ prefetchChapterCount: value })}
              onCurrentPasswordChange={setCurrentPassword}
              onNextPasswordChange={setNextPassword}
              onConfirmPasswordChange={setConfirmPassword}
              onChangePassword={handleChangeOwnPassword}
            />

            <Card className="overflow-hidden py-0">
              <div className="flex h-16 items-center border-b px-6">
                <CardTitle className="flex items-center gap-2">
                  <Database className="size-5" />
                  我的书源管理
                </CardTitle>
              </div>
              <SourceManagementPanel
                groups={sourceTabs}
                enabledSourceIds={workspace.enabledSourceIds}
                busy={workspace.busy}
                actionBusyId={workspace.sourceActionBusyId}
                onCheckSources={handleCheckSources}
                onSetSourcesEnabled={handleSetSourcesEnabled}
                onCreateGroup={handleCreateSourceGroup}
                onDeleteGroup={handleDeleteSourceGroup}
                onUpdateGroupSourceIds={handleUpdateSourceGroupSourceIds}
                onSaveGroups={handleSaveSourceGroups}
              />
            </Card>
          </>
        )}

        {isAdmin && activeSection === "accounts" && (
          <>
            <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold">账户管理</h2>
                <p className="text-sm text-muted-foreground">管理用户状态、角色、密码和书源授权</p>
              </div>
              <Button type="button" onClick={() => setCreateOpen(current => !current)}>
                {createOpen ? "收起" : "创建新账户"}
              </Button>
            </header>

            {createOpen && (
              <Card className="overflow-hidden">
                <CardContent className="pt-6">
                  <form className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto]" onSubmit={handleCreateUser}>
                    <Input value={newUsername} onChange={event => setNewUsername(event.target.value)} placeholder="用户名" required />
                    <Input value={newDisplayName} onChange={event => setNewDisplayName(event.target.value)} placeholder="显示名" />
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={event => setNewPassword(event.target.value)}
                      placeholder="初始密码"
                      required
                    />
                    <Button type="submit">创建</Button>
                  </form>
                </CardContent>
              </Card>
            )}

            <AccountTable
              query={query}
              filter={filter}
              sort={sort}
              users={visibleUsers}
              groups={groups}
              selectedUser={selectedUser}
              onQueryChange={setQuery}
              onFilterChange={setFilter}
              onSortChange={setSort}
              onSelectUser={setSelectedUserId}
            />

            <SelectedUserPanel
              user={selectedUser}
              groups={groups}
              sources={workspace.sources}
              displayNameDraft={displayNameDraft}
              resetPassword={resetPassword}
              onDisplayNameDraftChange={setDisplayNameDraft}
              onResetPasswordChange={setResetPassword}
              onUpdateUser={(user, patch) => void handleUpdateUser(user, patch)}
              onResetPassword={handleResetPassword}
            />
          </>
        )}

        {isAdmin && activeSection === "groups" && (
          <UserGroupPanel
            groups={groups}
            sources={workspace.sources}
            sourceById={sourceById}
            newGroupName={newGroupName}
            onNewGroupNameChange={setNewGroupName}
            onAddGroup={handleAddGroup}
            onDeleteGroup={groupId => void handleDeleteGroup(groupId)}
            onToggleGroupSource={(group, sourceId) => void handleToggleGroupSource(group, sourceId)}
            onSetGroupSources={(group, sourceIds) => void handleSetGroupSources(group, sourceIds)}
          />
        )}
      </div>
    </div>
  );
}

function AdminSidebar({
  activeSection,
  isAdmin,
  onSectionChange,
}: {
  activeSection: AdminSection;
  isAdmin: boolean;
  onSectionChange: (section: AdminSection) => void;
}) {
  const items: Array<{ id: AdminSection; label: string }> = [
    { id: "profile", label: "个人管理" },
    { id: "accounts", label: "账户管理" },
    { id: "groups", label: "组管理" },
  ].filter(item => item.id === "profile" || isAdmin);

  return (
    <aside className="rounded-2xl border bg-card p-3 lg:min-h-[calc(100dvh-8rem)]">
      <nav className="space-y-1">
        {items.map(item => {
          const active = item.id === activeSection;

          return (
            <button
              key={item.id}
              type="button"
              className={`relative flex w-full items-center rounded-lg px-4 py-2.5 text-left text-sm transition-colors ${
                active ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-accent/60 hover:text-accent-foreground"
              }`}
              onClick={() => onSectionChange(item.id)}
            >
              {active && <span className="absolute left-0 top-1/2 h-7 w-1 -translate-y-1/2 rounded-r-full bg-primary" />}
              <span className="pl-2">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

function PersonalManagementPanel({
  currentUser,
  prefetchChapterCount,
  currentPassword,
  nextPassword,
  confirmPassword,
  onPrefetchChapterCountChange,
  onCurrentPasswordChange,
  onNextPasswordChange,
  onConfirmPasswordChange,
  onChangePassword,
}: {
  currentUser: UserSpace | null;
  prefetchChapterCount: number;
  currentPassword: string;
  nextPassword: string;
  confirmPassword: string;
  onPrefetchChapterCountChange: (value: number) => void;
  onCurrentPasswordChange: (value: string) => void;
  onNextPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onChangePassword: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-3">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground">
              {getAvatarText(currentUser)}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-lg font-semibold">{currentUser?.displayName ?? "-"}</h2>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {currentUser?.role === "admin" ? "管理员" : "用户"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">@{currentUser?.username ?? "-"}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-sm md:ml-auto">
            <ProfileMeta label="状态" value={currentUser?.status === "active" ? "启用" : "停用"} />
            <ProfileMeta label="用户 ID" value={currentUser ? formatUserId(currentUser.id) : "-"} />
          </div>
        </div>

        <section className="rounded-2xl border bg-background p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-medium">正文预缓存</h3>
              <p className="mt-1 text-sm text-muted-foreground">打开章节时自动缓存后续章节，离线时可直接读取已缓存内容</p>
            </div>
            <div className="flex items-center gap-2 self-start sm:self-auto">
              <Button
                type="button"
                size="icon-sm"
                variant="outline"
                onClick={() => onPrefetchChapterCountChange(Math.max(0, prefetchChapterCount - 1))}
              >
                -
              </Button>
              <span className="w-16 text-center text-sm text-muted-foreground">{prefetchChapterCount}章</span>
              <Button
                type="button"
                size="icon-sm"
                variant="outline"
                onClick={() => onPrefetchChapterCountChange(Math.min(20, prefetchChapterCount + 1))}
              >
                +
              </Button>
            </div>
          </div>
        </section>

        <form onSubmit={onChangePassword}>
          <div className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
            <Input
              type="password"
              value={currentPassword}
              onChange={event => onCurrentPasswordChange(event.target.value)}
              placeholder="当前密码"
              autoComplete="current-password"
              required
            />
            <Input
              type="password"
              value={nextPassword}
              onChange={event => onNextPasswordChange(event.target.value)}
              placeholder="新密码"
              autoComplete="new-password"
              required
            />
            <Input
              type="password"
              value={confirmPassword}
              onChange={event => onConfirmPasswordChange(event.target.value)}
              placeholder="确认新密码"
              autoComplete="new-password"
              name="confirmPassword"
              required
            />
            <Button type="submit" className="md:w-32">保存</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function ProfileMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-2 font-medium">{value}</span>
    </div>
  );
}

function Picker({
  value,
  options,
  disabled = false,
  className,
  leadingIcon,
  onValueChange,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
  className?: string;
  leadingIcon?: React.ReactNode;
  onValueChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedOption = options.find(option => option.value === value);

  function handleSelect(nextValue: string) {
    onValueChange(nextValue);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn("h-10 justify-between px-3 font-normal", className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            {leadingIcon}
            <span className="truncate">{selectedOption?.label ?? "请选择"}</span>
          </span>
          <ChevronsUpDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-1">
        <div className="scroll-area max-h-64 overflow-y-auto">
          {options.map(option => {
            const selected = option.value === value;

            return (
              <button
                key={option.value}
                type="button"
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-hidden hover:bg-accent hover:text-accent-foreground",
                  selected && "bg-accent text-accent-foreground",
                )}
                onClick={() => handleSelect(option.value)}
              >
                <span className="min-w-0 truncate">{option.label}</span>
                {selected && <Check className="size-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AccountTable({
  query,
  filter,
  sort,
  users,
  groups,
  selectedUser,
  onQueryChange,
  onFilterChange,
  onSortChange,
  onSelectUser,
}: {
  query: string;
  filter: UserFilter;
  sort: UserSort;
  users: UserSpace[];
  groups: UserGroup[];
  selectedUser?: UserSpace;
  onQueryChange: (query: string) => void;
  onFilterChange: (filter: UserFilter) => void;
  onSortChange: (sort: UserSort) => void;
  onSelectUser: (userId: string) => void;
}) {
  return (
    <Card className="overflow-hidden py-0">
      <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center">
        <div className="flex min-w-0 flex-1">
          <Input
            value={query}
            onChange={event => onQueryChange(event.target.value)}
            placeholder="搜索用户..."
            className="h-10 rounded-r-none"
          />
          <Button type="button" variant="outline" className="h-10 rounded-l-none border-l-0 px-3">
            <Search className="size-4" />
          </Button>
        </div>
        <Picker
          value={filter}
          options={userFilterOptions}
          className="w-full md:w-32"
          leadingIcon={<SlidersHorizontal className="size-4" />}
          onValueChange={value => onFilterChange(value as UserFilter)}
        />
        <Picker
          value={sort}
          options={userSortOptions}
          className="w-full md:w-36"
          onValueChange={value => onSortChange(value as UserSort)}
        />
      </div>

      <div className="scroll-area overflow-x-auto">
        <table className="w-full min-w-[920px] text-sm">
          <thead className="border-b bg-muted/20 text-left text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">ID</th>
              <th className="px-4 py-3 font-medium">用户名</th>
              <th className="px-4 py-3 font-medium">显示名</th>
              <th className="px-4 py-3 text-center font-medium">已激活</th>
              <th className="px-4 py-3 text-center font-medium">角色</th>
              <th className="px-4 py-3 font-medium">用户组</th>
              <th className="px-4 py-3 font-medium">创建时间</th>
              <th className="px-4 py-3 text-right font-medium">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {users.map(user => (
              <tr key={user.id} className={selectedUser?.id === user.id ? "bg-accent/40" : "hover:bg-accent/25"}>
                <td className="px-4 py-3 text-muted-foreground">{formatUserId(user.id)}</td>
                <td className="px-4 py-3">
                  <button type="button" className="text-primary hover:underline" onClick={() => onSelectUser(user.id)}>
                    {user.username}
                  </button>
                  {user.role === "admin" && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">管理员</span>}
                </td>
                <td className="px-4 py-3">{user.displayName}</td>
                <td className="px-4 py-3">
                  <BooleanMark active={user.status === "active"} />
                </td>
                <td className="px-4 py-3 text-center">{user.role === "admin" ? "管理员" : "用户"}</td>
                <td className="px-4 py-3 text-muted-foreground">{formatGroupNames(user.groupIds, groups)}</td>
                <td className="px-4 py-3">{formatDate(user.createdAt)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <IconButton label="编辑" onClick={() => onSelectUser(user.id)}>
                      <Pencil className="size-4" />
                    </IconButton>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td className="px-4 py-10 text-center text-muted-foreground" colSpan={8}>没有匹配的用户</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function UserGroupPanel({
  groups,
  sources,
  sourceById,
  newGroupName,
  onNewGroupNameChange,
  onAddGroup,
  onDeleteGroup,
  onToggleGroupSource,
  onSetGroupSources,
}: {
  groups: UserGroup[];
  sources: SourceManifest[];
  sourceById: Map<string, SourceManifest>;
  newGroupName: string;
  onNewGroupNameChange: (value: string) => void;
  onAddGroup: (event: FormEvent<HTMLFormElement>) => void;
  onDeleteGroup: (groupId: string) => void;
  onToggleGroupSource: (group: UserGroup, sourceId: string) => void;
  onSetGroupSources: (group: UserGroup, sourceIds: string[]) => void;
}) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b">
        <CardTitle className="flex items-center gap-2">
          <Users className="size-5" />
          用户组
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <form className="flex gap-2" onSubmit={onAddGroup}>
          <Input value={newGroupName} onChange={event => onNewGroupNameChange(event.target.value)} placeholder="新用户组名称" />
          <Button type="submit" variant="secondary">添加</Button>
        </form>
        <div className="space-y-2">
          {groups.length === 0 && <p className="text-sm text-muted-foreground">还没有用户组</p>}
          {groups.map(group => (
            <div key={group.id} className="rounded-2xl border bg-background p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="font-medium">{group.name}</p>
                  <p className="text-xs text-muted-foreground">{group.sourceIds.length} 个书源</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button type="button" variant="ghost" size="sm" onClick={() => onSetGroupSources(group, sources.map(source => source.id))}>
                    全选
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => onSetGroupSources(group, [])}>
                    取消全部
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => onDeleteGroup(group.id)}>删除</Button>
                </div>
              </div>
              <div className="scroll-area mt-3 flex max-h-28 flex-wrap gap-2 overflow-y-auto">
                {sources.map(source => (
                  <TogglePill
                    key={source.id}
                    active={group.sourceIds.includes(source.id)}
                    label={sourceById.get(source.id)?.name ?? source.id}
                    onClick={() => onToggleGroupSource(group, source.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SelectedUserPanel({
  user,
  groups,
  sources,
  displayNameDraft,
  resetPassword,
  onDisplayNameDraftChange,
  onResetPasswordChange,
  onUpdateUser,
  onResetPassword,
}: {
  user?: UserSpace;
  groups: UserGroup[];
  sources: SourceManifest[];
  displayNameDraft: string;
  resetPassword: string;
  onDisplayNameDraftChange: (value: string) => void;
  onResetPasswordChange: (value: string) => void;
  onUpdateUser: (user: UserSpace, patch: Partial<Pick<UserSpace, "displayName" | "role" | "status" | "groupIds" | "sourceIds">>) => void;
  onResetPassword: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const passwordResetDisabled = !user || user.username === "root";

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b">
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="size-5" />
          {user ? `${user.displayName} 的权限` : "选择用户"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!user && <p className="text-sm text-muted-foreground">点击表格右侧操作选择一个用户</p>}
        {user && (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <Input
                value={displayNameDraft}
                onChange={event => onDisplayNameDraftChange(event.target.value)}
                onBlur={() => {
                  if (displayNameDraft.trim() !== user.displayName) onUpdateUser(user, { displayName: displayNameDraft });
                }}
                aria-label="显示名"
              />
              <Picker
                value={user.role}
                options={userRoleOptions}
                disabled={user.username === "root"}
                onValueChange={role => onUpdateUser(user, { role: role as UserSpace["role"] })}
              />
              <Picker
                value={user.status}
                options={userStatusOptions}
                disabled={user.username === "root"}
                onValueChange={status => onUpdateUser(user, { status: status as UserSpace["status"] })}
              />
            </div>

            <form className="flex gap-2" onSubmit={onResetPassword}>
              <Input
                type="password"
                value={resetPassword}
                onChange={event => onResetPasswordChange(event.target.value)}
                placeholder="新密码"
                disabled={passwordResetDisabled}
              />
              <Button type="submit" variant="secondary" disabled={passwordResetDisabled}>
                <KeyRound className="size-4" />
                重置密码
              </Button>
            </form>

            <PermissionBlock title="用户组">
              {groups.length === 0 && <span className="text-sm text-muted-foreground">暂无用户组</span>}
              {groups.map(group => (
                <TogglePill
                  key={group.id}
                  active={user.groupIds.includes(group.id)}
                  label={group.name}
                  onClick={() => onUpdateUser(user, { groupIds: toggleId(user.groupIds, group.id) })}
                />
              ))}
            </PermissionBlock>

            <PermissionBlock title="单独授权书源">
              {sources.map(source => (
                <TogglePill
                  key={source.id}
                  active={user.sourceIds.includes(source.id)}
                  label={source.name}
                  onClick={() => onUpdateUser(user, { sourceIds: toggleId(user.sourceIds, source.id) })}
                />
              ))}
            </PermissionBlock>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PermissionBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2 rounded-2xl border bg-background p-3">
      <p className="text-sm font-medium">{title}</p>
      <div className="scroll-area flex max-h-32 flex-wrap gap-2 overflow-y-auto">{children}</div>
    </section>
  );
}

function TogglePill({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className={`rounded-full border px-3 py-1 text-sm transition-colors ${
        active ? "border-foreground bg-foreground text-background" : "bg-card text-muted-foreground hover:bg-accent hover:text-accent-foreground"
      }`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function BooleanMark({ active }: { active: boolean }) {
  return (
    <span className={`mx-auto flex size-5 items-center justify-center ${active ? "text-foreground" : "text-muted-foreground"}`}>
      {active ? <Check className="size-4" /> : <X className="size-4" />}
    </span>
  );
}

function IconButton({ label, children, onClick }: { label: string; children: React.ReactNode; onClick: () => void }) {
  return (
    <button type="button" aria-label={label} className="rounded-md p-1.5 text-primary hover:bg-accent" onClick={onClick}>
      {children}
    </button>
  );
}

function filterAndSortUsers(users: UserSpace[], query: string, filter: UserFilter, sort: UserSort): UserSpace[] {
  const keyword = query.trim().toLocaleLowerCase();
  const filtered = users.filter(user => {
    if (filter === "active" && user.status !== "active") return false;
    if (filter === "disabled" && user.status !== "disabled") return false;
    if (filter === "admin" && user.role !== "admin") return false;
    if (filter === "user" && user.role !== "user") return false;
    if (!keyword) return true;
    return `${user.username} ${user.displayName}`.toLocaleLowerCase().includes(keyword);
  });

  return [...filtered].sort((left, right) => {
    if (sort === "created-asc") return left.createdAt.localeCompare(right.createdAt);
    if (sort === "username-asc") return left.username.localeCompare(right.username);
    if (sort === "username-desc") return right.username.localeCompare(left.username);
    return right.createdAt.localeCompare(left.createdAt);
  });
}

function readAdminSectionFromLocation(): AdminSection {
  const section = new URL(window.location.href).searchParams.get("section");
  if (section === "accounts" || section === "groups") return section;
  return "profile";
}

function formatUserId(id: string): string {
  const suffix = id.split("_").pop() ?? id;
  return suffix.toUpperCase();
}

function getAvatarText(user: UserSpace | null): string {
  const value = user?.displayName || user?.username || "?";
  return value.trim().slice(0, 1).toLocaleUpperCase();
}

function formatGroupNames(groupIds: string[], groups: UserGroup[]): string {
  if (groupIds.length === 0) return "-";
  const names = groupIds.map(id => groups.find(group => group.id === id)?.name ?? id);
  return names.join("、");
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function toggleId(values: string[], id: string): string[] {
  return values.includes(id) ? values.filter(item => item !== id) : [...values, id];
}
